import {http, HttpResponse} from 'msw';

import {isNil} from 'ramda';

import {
  CheckoutProfileRuleIdsArgSchema,
  CheckoutProfileRuleWriteSchema,
  PreviewCheckoutProfileRuleArgSchema,
  SwapCheckoutProfileRulePriorityArgSchema,
  type CheckoutProfileRule,
  type CheckoutProfileRuleWrite,
} from '../types';
import {
  checkoutProfileMockRefusal,
  type CheckoutProfileMockRefusal,
} from './checkoutProfileMockRefusal';
import type {CheckoutProfilesMockState} from './createCheckoutProfilesMockState';
import {getCheckoutProfileMockShapeRefusals} from './getCheckoutProfileMockShapeRefusals';
import {
  getCheckoutProfileRuleConflicts,
  previewCheckoutProfileRules,
} from './previewCheckoutProfileRules';

const RULE_URL = '*/checkout/v1/checkout-profile-rule';
const RULES_URL = '*/checkout/v1/checkout-profile-rules';

function ruleWriteRefusals(
  state: CheckoutProfilesMockState,
  body: CheckoutProfileRuleWrite & {id?: string},
  checkActivation = true
) {
  const refusals: CheckoutProfileMockRefusal[] = [];
  const profile = state.profiles.get(body.checkoutProfileId);
  if (isNil(profile)) {
    return [
      {code: 'PROFILE_NOT_FOUND', path: 'checkoutProfileId', message: body.checkoutProfileId},
    ];
  }
  if (body.branchId !== profile.branchId) {
    refusals.push({code: 'RULE_PROFILE_BRANCH_MISMATCH', path: 'branchId', message: profile.name});
  }
  if (checkActivation && body.isActive && !profile.isActive) {
    refusals.push({
      code: 'RULE_PROFILE_INACTIVE',
      path: 'checkoutProfileId',
      message: profile.name,
    });
  }
  const variants = state.variants.filter(
    (variant) => variant.branchId === body.branchId && variant.sourceType === body.sourceType
  );
  const allowed =
    body.sourceType === 'service-order'
      ? {
          serviceCaseTypeIds: variants.map((variant) => variant.parentTypeId),
          serviceOrderTypeIds: variants.map((variant) => variant.typeId),
          serviceOrderVariantIds: variants.map((variant) => variant.id),
        }
      : {
          authorizationProfileIds: variants.map((variant) => variant.parentTypeId),
          directSaleTypeIds: variants.map((variant) => variant.typeId),
          directSaleVariantIds: variants.map((variant) => variant.id),
        };
  Object.entries(allowed).forEach(([field, ids]) => {
    const selected = Object.entries(body.conditions).find(([key]) => key === field)?.[1];
    if (selected?.some((id) => !ids.includes(id))) {
      refusals.push({
        code: 'CONDITION_VALUE_NOT_FOUND',
        path: `conditions.${field}`,
        message: field,
      });
    }
  });
  const conflicts = getCheckoutProfileRuleConflicts(
    body,
    [...state.rules.values()],
    state.profiles,
    state.variants
  );
  if (checkActivation && body.isActive && conflicts.length) {
    refusals.push({
      code: 'RULE_CONFLICT',
      message: conflicts.map((rule) => rule.name).join(', '),
      rules: conflicts,
    });
  }
  return refusals;
}

function expandRule(
  state: CheckoutProfilesMockState,
  rule: CheckoutProfileRule
): CheckoutProfileRule {
  const profile = state.profiles.get(rule.checkoutProfileId);
  return profile
    ? {
        ...rule,
        checkoutProfile: {
          id: profile.id,
          name: profile.name,
          typeOfSale: profile.typeOfSale,
          isActive: profile.isActive,
        },
      }
    : rule;
}

function buildRule(
  state: CheckoutProfilesMockState,
  body: CheckoutProfileRuleWrite,
  stored?: CheckoutProfileRule
): CheckoutProfileRule {
  const profile = state.profiles.get(body.checkoutProfileId);
  if (isNil(profile)) {
    throw new Error('Profile must be validated before building a rule');
  }
  const timestamp = state.nextVersion();
  return {
    ...body,
    id: stored?.id ?? crypto.randomUUID(),
    checkoutProfile: {
      id: profile.id,
      name: profile.name,
      typeOfSale: profile.typeOfSale,
      isActive: profile.isActive,
    },
    createdAt: stored?.createdAt ?? timestamp,
    createdBy: stored?.createdBy ?? 'mock-administrator',
    updatedAt: timestamp,
    updatedBy: 'mock-administrator',
  };
}

export function createCheckoutProfileRuleHandlers(state: CheckoutProfilesMockState) {
  const stateChange = (isActive: boolean) =>
    http.post(`${RULES_URL}/${isActive ? 'activate' : 'deactivate'}`, async ({request}) => {
      const parsed = CheckoutProfileRuleIdsArgSchema.safeParse(await request.json());
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'RULE_NAME_REQUIRED')
        );
      }
      const refusals: CheckoutProfileMockRefusal[] = [];
      const selected: CheckoutProfileRule[] = [];
      parsed.data.checkoutProfileRuleIds.forEach((id, index) => {
        const path = `checkoutProfileRuleIds[${index}]`;
        const rule = state.rules.get(id);
        if (isNil(rule)) {
          refusals.push({code: 'RULE_NOT_FOUND', message: id, path});
          return;
        }
        selected.push(rule);
        refusals.push(
          ...ruleWriteRefusals(state, {...rule, isActive}).map((refusal) => ({
            ...refusal,
            path,
          }))
        );
      });
      if (refusals.length) {
        return checkoutProfileMockRefusal(refusals);
      }
      selected.forEach((rule) =>
        state.rules.set(rule.id, {...rule, isActive, updatedAt: state.nextVersion()})
      );
      return new HttpResponse(null, {status: 204});
    });

  return [
    http.get<{checkoutProfileRuleId: string}>(`${RULE_URL}/:checkoutProfileRuleId`, ({params}) => {
      const rule = state.rules.get(params.checkoutProfileRuleId);
      return rule
        ? HttpResponse.json(expandRule(state, rule))
        : checkoutProfileMockRefusal(
            [{code: 'RULE_NOT_FOUND', message: params.checkoutProfileRuleId}],
            404
          );
    }),
    http.post(RULE_URL, async ({request}) => {
      const parsed = CheckoutProfileRuleWriteSchema.safeParse(await request.json());
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'RULE_NAME_REQUIRED')
        );
      }
      const refusals = ruleWriteRefusals(state, parsed.data);
      if (refusals.length) {
        return checkoutProfileMockRefusal(refusals);
      }
      const rule = buildRule(state, parsed.data);
      state.rules.set(rule.id, rule);
      return HttpResponse.json(rule, {status: 201});
    }),
    http.put<{checkoutProfileRuleId: string}>(
      `${RULE_URL}/:checkoutProfileRuleId`,
      async ({request, params}) => {
        const stored = state.rules.get(params.checkoutProfileRuleId);
        if (isNil(stored)) {
          return checkoutProfileMockRefusal(
            [{code: 'RULE_NOT_FOUND', message: params.checkoutProfileRuleId}],
            404
          );
        }
        const parsed = CheckoutProfileRuleWriteSchema.safeParse(await request.json());
        if (!parsed.success) {
          return checkoutProfileMockRefusal(
            getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'RULE_NAME_REQUIRED')
          );
        }
        const refusals = ruleWriteRefusals(state, {...parsed.data, id: stored.id});
        if (refusals.length) {
          return checkoutProfileMockRefusal(refusals);
        }
        const rule = buildRule(state, parsed.data, stored);
        state.rules.set(rule.id, rule);
        return HttpResponse.json(rule);
      }
    ),
    http.post<{checkoutProfileRuleId: string}>(
      `${RULE_URL}/:checkoutProfileRuleId/delete`,
      ({params}) => {
        if (!state.rules.delete(params.checkoutProfileRuleId)) {
          return checkoutProfileMockRefusal(
            [{code: 'RULE_NOT_FOUND', message: params.checkoutProfileRuleId}],
            404
          );
        }
        return new HttpResponse(null, {status: 204});
      }
    ),
    http.post(`${RULE_URL}/preview`, async ({request}) => {
      const parsed = PreviewCheckoutProfileRuleArgSchema.safeParse({body: await request.json()});
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          getCheckoutProfileMockShapeRefusals(parsed.error.issues, 'RULE_NAME_REQUIRED')
        );
      }
      const refusals = ruleWriteRefusals(state, parsed.data.body, false);
      if (refusals.length) {
        return checkoutProfileMockRefusal(refusals);
      }
      return HttpResponse.json(
        previewCheckoutProfileRules(
          parsed.data.body,
          [...state.rules.values()],
          state.profiles,
          state.variants
        )
      );
    }),
    stateChange(true),
    stateChange(false),
    http.post(`${RULES_URL}/swap-priority`, async ({request}) => {
      const parsed = SwapCheckoutProfileRulePriorityArgSchema.safeParse(await request.json());
      if (!parsed.success) {
        return checkoutProfileMockRefusal(
          parsed.error.issues.map((issue) => ({
            code: 'RULE_SWAP_INVALID',
            path: 'checkoutProfileRuleIds',
            message: issue.message,
          }))
        );
      }
      const [firstId, secondId] = parsed.data.checkoutProfileRuleIds;
      const first = state.rules.get(firstId);
      const second = state.rules.get(secondId);
      if (isNil(first) || isNil(second)) {
        return checkoutProfileMockRefusal(
          [{code: 'RULE_NOT_FOUND', message: `${firstId}, ${secondId}`}],
          404
        );
      }
      const updated = [
        {
          ...first,
          priority:
            first.priority === second.priority
              ? first.priority + 10
              : Math.max(first.priority, second.priority),
        },
        {...second, priority: Math.min(first.priority, second.priority)},
      ];
      const candidates = [...state.rules.values()]
        .filter((rule) => rule.id !== firstId && rule.id !== secondId)
        .concat(updated);
      const conflicts = updated
        .filter((rule) => rule.isActive)
        .flatMap((rule) =>
          getCheckoutProfileRuleConflicts(rule, candidates, state.profiles, state.variants)
        );
      if (conflicts.length) {
        return checkoutProfileMockRefusal([
          {
            code: 'RULE_CONFLICT',
            message: conflicts.map((rule) => rule.name).join(', '),
            rules: conflicts,
          },
        ]);
      }
      const saved = updated.map((rule) => ({...rule, updatedAt: state.nextVersion()}));
      saved.forEach((rule) => state.rules.set(rule.id, rule));
      return HttpResponse.json(saved.map((rule) => expandRule(state, rule)));
    }),
  ];
}
